/**
 * Remark plugin that strips MDX-specific AST nodes (JSX elements, JS expressions,
 * and ESM import/export statements) so that MDX files can be rendered as plain
 * markdown. JSX flow elements that contain markdown children (e.g. `<Callout>some text</Callout>`)
 * are replaced by their children so the inner content is still rendered.
 *
 * Must be used together with `remark-mdx` which parses the MDX syntax into the AST.
 */
import type { Root, RootContent } from "mdast";
import type { Plugin } from "unified";

// MDX AST node types produced by remark-mdx / mdast-util-mdx
const MDX_JSX_FLOW = "mdxJsxFlowElement";
const MDX_JSX_TEXT = "mdxJsxTextElement";
const MDX_FLOW_EXPRESSION = "mdxFlowExpression";
const MDX_TEXT_EXPRESSION = "mdxTextExpression";
const MDX_ESM = "mdxjsEsm";

type MdxNode = RootContent & {
    children?: RootContent[];
};

/**
 * Recursively walk the AST and strip MDX nodes.
 * - JSX elements with children: replaced by their children (unwrapped)
 * - JSX elements without children, expressions, and ESM: removed entirely
 */
function stripMdxNodes(nodes: RootContent[]): RootContent[] {
    const result: RootContent[] = [];

    for (const node of nodes) {
        const type = node.type;

        if (type === MDX_ESM || type === MDX_FLOW_EXPRESSION || type === MDX_TEXT_EXPRESSION) {
            // Drop import/export statements and JS expressions entirely
            continue;
        }

        if (type === MDX_JSX_FLOW || type === MDX_JSX_TEXT) {
            const mdxNode = node as MdxNode;
            if (mdxNode.children && mdxNode.children.length > 0) {
                // Unwrap: replace the JSX element with its children
                result.push(...stripMdxNodes(mdxNode.children));
            }
            // Self-closing JSX tags with no children are dropped
            continue;
        }

        // For non-MDX nodes, recurse into children if present
        const parent = node as MdxNode;
        if (parent.children && Array.isArray(parent.children)) {
            parent.children = stripMdxNodes(parent.children);
        }

        result.push(node);
    }

    return result;
}

export const remarkStripMdx: Plugin<[], Root> = () => {
    return (tree: Root) => {
        tree.children = stripMdxNodes(tree.children);
    };
};
